import React from 'react';
import { flattenToAppURL, toPublicURL } from '@plone/volto/helpers/Url/Url';
import { NEWS_PORTAL_TYPES } from 'volto-ministerio-publico-federal/constants';
import useFormattedDate from 'volto-ministerio-publico-federal/hooks/useFormattedDate';
import usePrintTimestamp from 'volto-ministerio-publico-federal/hooks/usePrintTimestamp';
import PrintQRCode from './PrintQRCode';

interface PrintFooterProps {
  /** `@id` do conteúdo, para montar a URL pública canônica. */
  contentId?: string;
  /** `@type` do conteúdo — decide entre QR code e URL em texto. */
  contentType?: string;
}

/**
 * Rodapé só-impressão: fecha a folha com o endereço da página e a assinatura
 * institucional.
 *
 * Na notícia o endereço vira um bloco com QR code, para quem tem o papel na
 * mão poder abrir a matéria no celular. Nos demais tipos continua sendo a URL
 * em texto — é o que já existia, e nada ali deve mudar.
 *
 * Fica no fluxo, fechando a matéria — e NÃO ancorado ao pé do papel como o
 * rodapé do Word; o porquê está em `theme/styles/_print.scss`.
 *
 * Fica no DOM de toda página (é renderizado pelo `View`), escondido na tela
 * e revelado por `@media print` — a visibilidade não usa `hidden print:block`
 * para não depender da utility `.block` do Tailwind, que colide com o
 * `.block` do editor do Volto.
 */
export const PrintFooter: React.FC<PrintFooterProps> = ({
  contentId,
  contentType,
}) => {
  const { formatShortDate } = useFormattedDate();
  const printedAt = usePrintTimestamp();

  /* `toPublicURL(flattenToAppURL(...))` é o mesmo caminho que o
     `canonicalUrl` do SEO (`components/SEO/metadata.ts`): resolve já no SSR,
     sem tocar em `window` e sem risco de hidratação. Ele monta
     `config.settings.publicURL` + o caminho — daí a URL só sai correta em um
     ambiente com `RAZZLE_PUBLIC_URL` definido; sem ela o Volto cai na origem
     de `RAZZLE_API_PATH`, que pode ser um host interno. Num QR impresso isso
     seria um defeito físico, sem chance de correção depois. */
  const url =
    contentId === undefined || contentId === null
      ? undefined
      : toPublicURL(flattenToAppURL(contentId) || '/');

  const isNoticia = NEWS_PORTAL_TYPES.includes(contentType ?? '');

  if (!url) return null;

  return (
    <footer className="mpf-print-footer" aria-hidden="true">
      {isNoticia ? (
        <PrintQRCode url={url} />
      ) : (
        <p className="mpf-print-footer__url">{url}</p>
      )}
      <p className="mpf-print-footer__stamp">
        Ministério Público Federal
        {printedAt && ` · Impresso em ${formatShortDate(printedAt)}`}
      </p>
    </footer>
  );
};

export default PrintFooter;
